import React, { useState } from 'react';
import {
  Shield, AlertTriangle, CheckCircle, FileText, Clock, 
  Search, Filter, Eye, Edit, Trash2, User, Calendar,
  TrendingUp, AlertCircle, Award, Lock, Database, Clipboard, Plus
} from 'lucide-react';

interface ComplianceRecord {
  id: string;
  title: string;
  type: 'regulatory' | 'safety' | 'quality' | 'training' | 'audit';
  status: 'compliant' | 'pending' | 'non_compliant' | 'under_review' | 'expired';
  dueDate: string;
  lastReview: string;
  assignedTo: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  description: string;
}

interface SafetyIncident {
  id: string;
  title: string;
  type: 'medication_error' | 'patient_safety' | 'equipment_failure' | 'workplace_injury' | 'security_breach';
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'reported' | 'investigating' | 'resolved' | 'closed';
  reportedDate: string;
  reportedBy: string;
  assignedTo: string;
  description: string;
}

interface AuditRecord {
  id: string;
  title: string;
  type: 'internal' | 'external' | 'regulatory' | 'quality' | 'security';
  status: 'scheduled' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  scheduledDate: string;
  auditor: string;
  scope: string;
  findings: string;
  score?: number;
}

const mockComplianceRecords: ComplianceRecord[] = [
  {
    id: 'COMP-001',
    title: 'DEA Controlled Substance Inventory',
    type: 'regulatory',
    status: 'compliant',
    dueDate: '2024-02-15',
    lastReview: '2024-01-15',
    assignedTo: 'Dr. Sarah Johnson',
    priority: 'high',
    description: 'Annual DEA controlled substance inventory and reporting requirements'
  },
  {
    id: 'COMP-002',
    title: 'OSHA Workplace Safety Training',
    type: 'training',
    status: 'pending',
    dueDate: '2024-01-30',
    lastReview: '2023-12-15',
    assignedTo: 'Jennifer Lee',
    priority: 'medium',
    description: 'Annual OSHA workplace safety training for all staff members'
  },
  {
    id: 'COMP-003',
    title: 'HIPAA Privacy Policy Review',
    type: 'regulatory',
    status: 'under_review',
    dueDate: '2024-02-01',
    lastReview: '2024-01-01',
    assignedTo: 'Dr. Michael Brown',
    priority: 'critical',
    description: 'Quarterly review of HIPAA privacy policies and procedures'
  },
  {
    id: 'COMP-004',
    title: 'Quality Assurance Program',
    type: 'quality',
    status: 'compliant',
    dueDate: '2024-03-01',
    lastReview: '2024-01-01',
    assignedTo: 'Dr. Sarah Johnson',
    priority: 'high',
    description: 'Annual quality assurance program review and updates'
  }
];

const mockSafetyIncidents: SafetyIncident[] = [
  {
    id: 'INC-001',
    title: 'Medication Dispensing Error',
    type: 'medication_error',
    severity: 'high',
    status: 'investigating',
    reportedDate: '2024-01-18',
    reportedBy: 'Dr. Sarah Johnson',
    assignedTo: 'Dr. Michael Brown',
    description: 'Patient received incorrect medication dosage. Immediate intervention prevented harm.'
  },
  {
    id: 'INC-002',
    title: 'Equipment Malfunction',
    type: 'equipment_failure',
    severity: 'medium',
    status: 'resolved',
    reportedDate: '2024-01-15',
    reportedBy: 'Jennifer Lee',
    assignedTo: 'David Chen',
    description: 'Automated dispensing machine malfunction. Repaired and tested successfully.'
  },
  {
    id: 'INC-003',
    title: 'Patient Fall Prevention',
    type: 'patient_safety',
    severity: 'low',
    status: 'closed',
    reportedDate: '2024-01-10',
    reportedBy: 'Dr. Michael Brown',
    assignedTo: 'Dr. Sarah Johnson',
    description: 'Near-miss incident with patient safety protocol. Additional training implemented.'
  }
];

const mockAuditRecords: AuditRecord[] = [
  {
    id: 'AUD-001',
    title: 'DEA Regulatory Compliance Audit',
    type: 'regulatory',
    status: 'completed',
    scheduledDate: '2024-01-10',
    auditor: 'DEA Inspector',
    scope: 'Controlled substance handling and record keeping',
    findings: 'All requirements met. No violations found.',
    score: 100
  },
  {
    id: 'AUD-002',
    title: 'Internal Quality Assurance Audit',
    type: 'internal',
    status: 'in_progress',
    scheduledDate: '2024-01-20',
    auditor: 'Dr. Sarah Johnson',
    scope: 'Medication dispensing processes and documentation',
    findings: 'Audit in progress. Preliminary findings show good compliance.',
    score: 85
  },
  {
    id: 'AUD-003',
    title: 'HIPAA Security Assessment',
    type: 'security',
    status: 'scheduled',
    scheduledDate: '2024-02-01',
    auditor: 'External Security Consultant',
    scope: 'Data security and privacy protection measures',
    findings: 'Scheduled for February 1st, 2024',
    score: undefined
  }
];

const getComplianceStatusColor = (status: string) => {
  switch (status) {
    case 'compliant': return 'border-green-500 bg-green-50 text-green-700';
    case 'pending': return 'border-yellow-500 bg-yellow-50 text-yellow-700';
    case 'non_compliant': return 'border-red-500 bg-red-50 text-red-700';
    case 'under_review': return 'border-blue-500 bg-blue-50 text-blue-700';
    case 'expired': return 'border-red-600 bg-red-100 text-red-800';
    default: return 'border-gray-500 bg-gray-50 text-gray-700';
  }
};

const getIncidentSeverityColor = (severity: string) => {
  switch (severity) {
    case 'low': return 'bg-green-100 text-green-800 border-green-200';
    case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'critical': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getIncidentStatusColor = (status: string) => {
  switch (status) {
    case 'reported': return 'border-blue-500 bg-blue-50 text-blue-700';
    case 'investigating': return 'border-yellow-500 bg-yellow-50 text-yellow-700';
    case 'resolved': return 'border-green-500 bg-green-50 text-green-700';
    case 'closed': return 'border-gray-500 bg-gray-50 text-gray-700';
    default: return 'border-gray-500 bg-gray-50 text-gray-700';
  }
};

const getAuditStatusColor = (status: string) => {
  switch (status) {
    case 'scheduled': return 'border-blue-500 bg-blue-50 text-blue-700';
    case 'in_progress': return 'border-yellow-500 bg-yellow-50 text-yellow-700';
    case 'completed': return 'border-green-500 bg-green-50 text-green-700';
    case 'failed': return 'border-red-500 bg-red-50 text-red-700';
    case 'cancelled': return 'border-gray-500 bg-gray-50 text-gray-700';
    default: return 'border-gray-500 bg-gray-50 text-gray-700';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'low': return 'bg-green-100 text-green-800 border-green-200';
    case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'critical': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export function Compliance() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const filteredCompliance = mockComplianceRecords.filter(record => {
    const matchesSearch = record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.assignedTo.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
    const matchesType = typeFilter === 'all' || record.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const complianceStats = {
    total: mockComplianceRecords.length,
    compliant: mockComplianceRecords.filter(r => r.status === 'compliant').length,
    pending: mockComplianceRecords.filter(r => r.status === 'pending').length,
    nonCompliant: mockComplianceRecords.filter(r => r.status === 'non_compliant').length,
    critical: mockComplianceRecords.filter(r => r.priority === 'critical').length
  };

  const incidentStats = {
    total: mockSafetyIncidents.length,
    open: mockSafetyIncidents.filter(i => i.status !== 'closed').length,
    resolved: mockSafetyIncidents.filter(i => i.status === 'resolved').length,
    critical: mockSafetyIncidents.filter(i => i.severity === 'critical').length
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#4B0082] via-[#6B38FB] to-purple-600 rounded-3xl p-10 text-white shadow-2xl shadow-[#6B38FB]/25">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
              <Shield className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-4xl font-black mb-2">Compliance & Safety</h1>
              <p className="text-xl text-purple-100">Regulatory compliance, safety protocols, and audit management</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Total Records</p>
              <p className="text-2xl font-black">{complianceStats.total}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Compliant</p>
              <p className="text-2xl font-black text-green-300">{complianceStats.compliant}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Pending</p>
              <p className="text-2xl font-black text-yellow-300">{complianceStats.pending}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Critical</p>
              <p className="text-2xl font-black text-red-300">{complianceStats.critical}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-purple-200 text-sm">Incidents</p>
              <p className="text-2xl font-black">{incidentStats.total}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search compliance records, incidents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
            >
              <option value="all">All Statuses</option>
              <option value="compliant">Compliant</option>
              <option value="pending">Pending</option>
              <option value="non_compliant">Non-Compliant</option>
              <option value="under_review">Under Review</option>
              <option value="expired">Expired</option>
            </select>
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#6B38FB]/20 focus:border-[#6B38FB] transition-all duration-300"
            >
              <option value="all">All Types</option>
              <option value="regulatory">Regulatory</option>
              <option value="safety">Safety</option>
              <option value="quality">Quality</option>
              <option value="training">Training</option>
              <option value="audit">Audit</option>
            </select>
          </div>
          
          <button className="px-6 py-3 bg-gradient-to-r from-[#4B0082] to-[#6B38FB] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6B38FB]/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>New Record</span>
          </button>
        </div>
      </div>

      {/* Compliance Records */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
          <h3 className="text-xl font-bold text-gray-900">Compliance Records</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned To</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCompliance.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">{record.title}</span>
                      <p className="text-xs text-gray-500 mt-1">{record.description}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 capitalize">{record.type.replace('_', ' ')}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getComplianceStatusColor(record.status)}`}>
                      {record.status.replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(record.priority)}`}>
                      {record.priority.charAt(0).toUpperCase() + record.priority.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{record.dueDate}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{record.assignedTo}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                        <Clipboard className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Safety Incidents and Audit Records */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Safety Incidents */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
            <h3 className="text-xl font-bold text-gray-900">Safety Incidents</h3>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {mockSafetyIncidents.map((incident) => (
                <div key={incident.id} className="group p-4 rounded-xl border-2 border-gray-200 hover:border-[#6B38FB] hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">{incident.title}</h4>
                    <div className="flex space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getIncidentSeverityColor(incident.severity)}`}>
                        {incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1)}
                      </span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getIncidentStatusColor(incident.status)}`}>
                        {incident.status.charAt(0).toUpperCase() + incident.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 space-y-2 mb-3">
                    <p className="flex items-center">
                      <Calendar className="w-3 h-3 mr-2" />
                      Reported: {incident.reportedDate}
                    </p>
                    <p className="flex items-center">
                      <User className="w-3 h-3 mr-2" />
                      By: {incident.reportedBy}
                    </p>
                    <p className="flex items-center">
                      <User className="w-3 h-3 mr-2" />
                      Assigned: {incident.assignedTo}
                    </p>
                  </div>
                  <p className="text-sm text-gray-700">{incident.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Audit Records */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#4B0082]/5 to-[#6B38FB]/5">
            <h3 className="text-xl font-bold text-gray-900">Audit Records</h3>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {mockAuditRecords.map((audit) => (
                <div key={audit.id} className="group p-4 rounded-xl border-2 border-gray-200 hover:border-[#6B38FB] hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">{audit.title}</h4>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${getAuditStatusColor(audit.status)}`}>
                      {audit.status.replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-2 mb-3">
                    <p className="flex items-center">
                      <Calendar className="w-3 h-3 mr-2" />
                      Scheduled: {audit.scheduledDate}
                    </p>
                    <p className="flex items-center">
                      <User className="w-3 h-3 mr-2" />
                      Auditor: {audit.auditor}
                    </p>
                    <p className="flex items-center">
                      <Database className="w-3 h-3 mr-2" />
                      Scope: {audit.scope}
                    </p>
                    {audit.score && (
                      <p className="flex items-center">
                        <Award className="w-3 h-3 mr-2" />
                        Score: {audit.score}/100
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">{audit.findings}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Compliance Check</h4>
              <p className="text-sm text-gray-600">Review requirements</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Safety Protocol</h4>
              <p className="text-sm text-gray-600">Update procedures</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Clipboard className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Audit Schedule</h4>
              <p className="text-sm text-gray-600">Plan assessments</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Reports</h4>
              <p className="text-sm text-gray-600">Generate insights</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
